<script lang="ts">
export const textareaCls = cva("textarea", {
	variants: {
		color: {
			default: null,
			neutral: "textarea-neutral",
			primary: "textarea-primary",
			secondary: "textarea-secondary",
			accent: "textarea-accent",
			info: "textarea-info",
			success: "textarea-success",
			warning: "textarea-warning",
			error: "textarea-error",
		},
		size: {
			xs: "textarea-xs",
			sm: "textarea-sm",
			md: "textarea-md",
			lg: "textarea-lg",
		},
		variant: {
			default: null,
			ghost: "textarea-ghost",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

type variant = VariantProps<typeof textareaCls>;

export interface TextAreaProps extends ClassProps {
	color?: variant["color"];
	variant?: variant["variant"];
	size?: variant["size"];
}

interface Props
	extends TextAreaProps,
		/* @vue-ignore */ Omit<TextareaHTMLAttributes, keyof TextAreaProps> {}
</script>

<script setup lang="ts">
import { cva, type VariantProps } from "class-variance-authority";
import { useForwardExpose } from "reka-ui";
import { computed, inject, useAttrs, type TextareaHTMLAttributes } from "vue";
import { InputGroupSymbol } from "../../injectionSymbols";
import type { ClassProps } from "../../lib/base-props";
import { cn, htmlAttrs } from "../../lib/utils";

const inGroup = inject(InputGroupSymbol, false);

defineOptions({ inheritAttrs: false });

const props = defineProps<Props>() as TextAreaProps;
const attrs = useAttrs() as TextareaHTMLAttributes;

const model = defineModel<any>();

const classes = computed(() => {
	if (inGroup) {
		return props.class;
	}

	return cn(textareaCls(props));
});

const { forwardRef } = useForwardExpose();
</script>

<template>
	<textarea
		:ref="forwardRef"
		v-model="model"
		placeholder=" "
		:class="classes"
		:data-size="size"
		:data-color="color"
		:data-variant="variant"
		v-bind="htmlAttrs(attrs)"
	/>
</template>
